import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { calcSaleCommission, getTimeOfDayTheme, calcGrowthLevel, defaultUser, defaultBonusTiers } from '../utils/commissionCalc';
import calcActivityTargets from '../utils/activityTargets';
import CAR_IMAGES from '../utils/carImages';

const AppContext = createContext();

export const useAppContext = () => useContext(AppContext);

export function AppProvider({
  children,
  initialUserData = defaultUser,
  initialSales = [],
  initialActivities = [],
  initialBonusTiers = defaultBonusTiers,
  initialVoiceMemos = [],
  initialLeads = [],
  initialAppointments = [],
  onDataChange = () => {},
  onLogout = () => {},
  userEmail = '',
  userUid = ''
}) {
  const [activeTab, setActiveTab]   = useState('dashboard');
  const [userData, setUserData]     = useState(initialUserData);
  const [bonusTiers, setBonusTiers] = useState(initialBonusTiers);
  const [sales, setSales]           = useState(initialSales);
  const [activities, setActivities] = useState(initialActivities);
  const [voiceMemos, setVoiceMemos] = useState(initialVoiceMemos);
  const [leads, setLeads]           = useState(initialLeads);
  const [appointments, setAppointments] = useState(initialAppointments);

  // Collapsible section state (persists across tab switches)
  const [collapsedSections, setCollapsedSections] = useState({});

  // AI Assistant state
  const [showAI, setShowAI] = useState(false);
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  // Theme & animations
  const [theme, setTheme] = useState(getTimeOfDayTheme());
  const growth = calcGrowthLevel(sales, activities, CAR_IMAGES);

  // Update theme every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setTheme(getTimeOfDayTheme());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Modals
  const [showAddSale, setShowAddSale]             = useState(false);
  const [editingSaleId, setEditingSaleId]         = useState(null);
  const [saleForm, setSaleForm] = useState({ customerName:'', vehicle:'', condition:'New', frontGross:0, backGross:0, spiffs:0, notes:'' });
  const [showAddActivity, setShowAddActivity]     = useState(false);
  const [activityForm, setActivityForm] = useState({ customerName:'', type:'Call' });
  const [showQuickTouch, setShowQuickTouch]       = useState(false);
  const [quickTouchCustomer, setQuickTouchCustomer] = useState('');

  // Celebration
  const [celebration, setCelebration] = useState(null);
  const prevCompleted = useRef({ calls: false, texts: false, emails: false });

  // Appointment reminder state
  const [activeReminder, setActiveReminder] = useState(null);
  const reminderShownRef = useRef(new Set());

  // Sync all state changes to Firebase
  useEffect(() => {
    onDataChange({
      settings: userData,
      sales,
      activities,
      bonusTiers,
      leads,
      appointments,
    });
  }, [userData, sales, activities, bonusTiers, leads, appointments]);

  // Auto-targets whenever goal changes
  useEffect(() => {
    if (userData.useAutoTargets) {
      const t = calcActivityTargets(userData.unitsGoal);
      setUserData(u => ({ ...u, callsTarget: t.dailyCalls, textsTarget: t.dailyTexts, emailsTarget: t.dailyEmails }));
    }
  }, [userData.unitsGoal, userData.useAutoTargets]);

  // ── Derived metrics ──────────────────────────────────────────────────────
  const today        = new Date();
  const currentDay   = today.getDate();
  const daysInMonth  = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const daysLeft     = daysInMonth - currentDay + 1;

  const unitsSold    = sales.length;
  const carsLeft     = Math.max(0, userData.unitsGoal - unitsSold);

  const newSales  = sales.filter(s => s.condition === 'New');
  const usedSales = sales.filter(s => s.condition === 'Used');

  const allFrontGross  = sales.reduce((a, s) => a + s.frontGross, 0);
  const allBackGross   = sales.reduce((a, s) => a + s.backGross, 0);
  const totalSpiffs    = sales.reduce((a, s) => a + s.spiffs, 0);
  const newFrontGross  = newSales.reduce((a, s) => a + s.frontGross, 0);
  const newBackGross   = newSales.reduce((a, s) => a + s.backGross, 0);
  const usedFrontGross = usedSales.reduce((a, s) => a + s.frontGross, 0);
  const usedBackGross  = usedSales.reduce((a, s) => a + s.backGross, 0);
  const newTotalGross  = newFrontGross + newBackGross;
  const usedTotalGross = usedFrontGross + usedBackGross;
  const totalGross     = newTotalGross + usedTotalGross;

  const commission     = sales.reduce((a, s) => a + calcSaleCommission(s, userData), 0);
  const unitBonuses    = newSales.length * userData.newUnitBonus + usedSales.length * userData.usedUnitBonus;
  const currentTier    = [...bonusTiers].reverse().find(t => unitsSold >= t.unitsSold);
  const tierBonus      = currentTier?.bonusAmount ?? 0;
  const totalEarnings  = commission + unitBonuses + tierBonus + totalSpiffs;

  const expectedUnits  = (userData.unitsGoal / daysInMonth) * currentDay;
  const paceDiff       = Math.round(unitsSold - expectedUnits);
  const unitsToCloseToday = Math.max(0, Math.ceil(carsLeft / daysLeft));

  // Today's activities
  const todayStr = today.toDateString();
  const todayActs = activities.filter(a => new Date(a.date).toDateString() === todayStr);
  const callsToday  = todayActs.filter(a => a.type === 'Call').length;
  const textsToday  = todayActs.filter(a => a.type === 'Text').length;
  const emailsToday = todayActs.filter(a => a.type === 'Email').length;
  const apptsToday  = todayActs.filter(a => a.type === 'Appointment').length;

  const callsPct  = Math.min(100, (callsToday  / userData.callsTarget)  * 100);
  const textsPct  = Math.min(100, (textsToday  / userData.textsTarget)  * 100);
  const emailsPct = Math.min(100, (emailsToday / userData.emailsTarget) * 100);
  const totalGoal = userData.callsTarget + userData.textsTarget + userData.emailsTarget;
  const totalDone = callsToday + textsToday + emailsToday;
  const overallPct = totalGoal > 0 ? Math.min(100, Math.round((totalDone / totalGoal) * 100)) : 0;

  // ── Upcoming appointments (sorted by date) ───────────────────────────────
  const upcomingAppointments = appointments
    .filter(a => a.status === 'upcoming' && a.appointmentDate)
    .sort((a, b) => {
      const dateA = new Date(a.appointmentDate);
      const dateB = new Date(b.appointmentDate);
      if (isNaN(dateA.getTime())) return 1;
      if (isNaN(dateB.getTime())) return -1;
      return dateA - dateB;
    });

  const todaysAppointments = upcomingAppointments.filter(a => {
    try {
      return new Date(a.appointmentDate).toDateString() === todayStr;
    } catch (e) { return false; }
  });

  // Celebration checks
  useEffect(() => {
    const now = { calls: callsToday >= userData.callsTarget, texts: textsToday >= userData.textsTarget, emails: emailsToday >= userData.emailsTarget };
    const prev = prevCompleted.current;
    if (now.calls && now.texts && now.emails && !(prev.calls && prev.texts && prev.emails)) {
      setCelebration('all');
    } else if (now.calls && !prev.calls) setCelebration('calls');
    else if (now.texts && !prev.texts)   setCelebration('texts');
    else if (now.emails && !prev.emails) setCelebration('emails');
    prevCompleted.current = now;
  }, [callsToday, textsToday, emailsToday]);

  useEffect(() => {
    if (celebration) { const t = setTimeout(() => setCelebration(null), 4000); return () => clearTimeout(t); }
  }, [celebration]);

  // ── Appointment Reminders ─────────────────────────────────────────────────
  useEffect(() => {
    // Request notification permission on mount
    try {
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }
    } catch (e) {
      // Notification API not supported on this device
    }
  }, []);

  useEffect(() => {
    const checkReminders = () => {
      try {
        const now = new Date();
        upcomingAppointments.forEach(appt => {
          const apptTime = new Date(appt.appointmentDate);
          if (isNaN(apptTime.getTime())) return; // Skip invalid dates
          const diffMs = apptTime - now;
          const diffMins = diffMs / 60000;

          // Remind 15 minutes before AND when it's time
          if (diffMins <= 15 && diffMins > -5 && !reminderShownRef.current.has(appt.id)) {
            reminderShownRef.current.add(appt.id);
            setActiveReminder(appt);

            // Browser notification
            try {
              if ('Notification' in window && Notification.permission === 'granted') {
                const timeLabel = diffMins <= 0 ? 'NOW' : `in ${Math.ceil(diffMins)} min`;
                new Notification(`🚗 Appointment ${timeLabel}!`, {
                  body: `${appt.customerName}${appt.vehicleOfInterest ? ' — ' + appt.vehicleOfInterest : ''}`,
                  icon: '/favicon.ico',
                  tag: `appt-${appt.id}`,
                });
              }
            } catch (e) {
              // Notification not supported
            }
          }
        });
      } catch (e) {
        console.error('Reminder check error:', e);
      }
    };

    checkReminders();
    const interval = setInterval(checkReminders, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, [upcomingAppointments]);

  // Auto-dismiss reminder after 30 seconds
  useEffect(() => {
    if (activeReminder) {
      const t = setTimeout(() => setActiveReminder(null), 30000);
      return () => clearTimeout(t);
    }
  }, [activeReminder]);

  // ── Collapsible section toggle ───────────────────────────────────────────
  const toggleSection = useCallback((key) => {
    setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // ── Quick touch logger ────────────────────────────────────────────────────
  const logTouch = (type, name) => {
    setActivities(prev => [...prev, {
      id: Date.now().toString(),
      customerName: name || 'Unknown',
      type,
      date: new Date().toISOString(),
    }]);
  };

  const logQuickBundle = (customerName) => {
    const now = new Date().toISOString();
    const base = Date.now();
    setActivities(prev => [...prev,
      { id: `${base}-1`, customerName, type: 'Call',  date: now },
      { id: `${base}-2`, customerName, type: 'Call',  date: now },
      { id: `${base}-3`, customerName, type: 'Call',  date: now },
      { id: `${base}-4`, customerName, type: 'Email', date: now },
      { id: `${base}-5`, customerName, type: 'Email', date: now },
      { id: `${base}-6`, customerName, type: 'Text',  date: now },
    ]);
  };

  const removeTouch = (type) => {
    const todayStr = new Date().toDateString();
    setActivities(prev => {
      const todayOfType = prev
        .filter(a => a.type === type && new Date(a.date).toDateString() === todayStr)
        .sort((a, b) => new Date(b.date) - new Date(a.date));
      if (todayOfType.length === 0) return prev;
      const removeId = todayOfType[0].id;
      return prev.filter(a => a.id !== removeId);
    });
  };

  // ── Leads CRUD ────────────────────────────────────────────────────────────
  const addLead = (lead) => {
    const newLead = {
      ...lead,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setLeads(prev => [...prev, newLead]);
    return newLead;
  };

  const updateLead = (id, updates) => {
    setLeads(prev => prev.map(l => l.id === id ? { ...l, ...updates, updatedAt: new Date().toISOString() } : l));
  };

  const deleteLead = (id) => {
    setLeads(prev => prev.filter(l => l.id !== id));
    // Also remove related appointments
    setAppointments(prev => prev.filter(a => a.leadId !== id));
  };

  // ── Appointments CRUD ─────────────────────────────────────────────────────
  const addAppointment = (appt) => {
    try {
      const newAppt = {
        ...appt,
        id: Date.now().toString(),
        status: 'upcoming',
        createdAt: new Date().toISOString(),
      };
      setAppointments(prev => [...prev, newAppt]);

      // Log an Appointment activity
      logTouch('Appointment', appt.customerName || 'Unknown');

      return newAppt;
    } catch (err) {
      console.error('addAppointment error:', err);
      return null;
    }
  };

  const completeAppointment = (id) => {
    setAppointments(prev => prev.map(a =>
      a.id === id ? { ...a, status: 'completed', completedAt: new Date().toISOString() } : a
    ));
    if (activeReminder?.id === id) setActiveReminder(null);
  };

  const noShowAppointment = (id) => {
    setAppointments(prev => prev.map(a =>
      a.id === id ? { ...a, status: 'no-show', completedAt: new Date().toISOString() } : a
    ));
    if (activeReminder?.id === id) setActiveReminder(null);
  };

  const deleteAppointment = (id) => {
    setAppointments(prev => prev.filter(a => a.id !== id));
    if (activeReminder?.id === id) setActiveReminder(null);
  };

  const value = {
    // Tab navigation
    activeTab, setActiveTab,
    // User data
    userData, setUserData,
    bonusTiers, setBonusTiers,
    // Sales
    sales, setSales,
    saleForm, setSaleForm,
    showAddSale, setShowAddSale,
    editingSaleId, setEditingSaleId,
    // Activities
    activities, setActivities,
    activityForm, setActivityForm,
    showAddActivity, setShowAddActivity,
    showQuickTouch, setShowQuickTouch,
    quickTouchCustomer, setQuickTouchCustomer,
    logTouch, logQuickBundle, removeTouch,
    // Voice memos
    voiceMemos, setVoiceMemos,
    // Collapsible sections
    collapsedSections, toggleSection,
    // Leads
    leads, setLeads, addLead, updateLead, deleteLead,
    // Appointments
    appointments, setAppointments,
    addAppointment, completeAppointment, noShowAppointment, deleteAppointment,
    upcomingAppointments, todaysAppointments,
    activeReminder, setActiveReminder,
    // AI
    showAI, setShowAI,
    aiMessages, setAiMessages,
    aiInput, setAiInput,
    aiLoading, setAiLoading,
    // Theme
    theme, growth,
    // Celebration
    celebration, setCelebration,
    // Derived metrics
    unitsSold, carsLeft, newSales, usedSales,
    allFrontGross, allBackGross, totalSpiffs,
    newFrontGross, newBackGross, usedFrontGross, usedBackGross,
    newTotalGross, usedTotalGross, totalGross,
    commission, unitBonuses, currentTier, tierBonus, totalEarnings,
    expectedUnits, paceDiff, unitsToCloseToday,
    todayActs, callsToday, textsToday, emailsToday, apptsToday,
    callsPct, textsPct, emailsPct, totalGoal, totalDone, overallPct,
    daysInMonth, daysLeft, currentDay,
    // Props
    onLogout, userEmail, userUid,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export default AppContext;
